/**
 * TRAIT : un disque blanc, et une signature a l'encre du client qui S'ECRIT quand elle arrive
 * sur la page, comme une plume qui passe.
 *
 * L'IDEE, EN UN MOT : editorial, luxe discret. Rien ne clignote, rien ne rebondit ; le seul
 * evenement est le geste d'ecriture, puis un eclat qui parcourt le trait de loin en loin.
 *
 * L'ARRIVEE EST UNE TRANSITION, PAS UNE ANIMATION, et c'est ce qui la rend reversible : la meme
 * propriete (stroke-dashoffset) revient a sa valeur cachee des que le panneau s'ouvre, donc la
 * signature s'efface d'elle-meme pour laisser la plume ecrire la croix. Une animation, elle,
 * aurait rejoue depuis le debut a chaque changement d'etat. La classe qui la declenche est posee
 * apres le montage — sur le conteneur du lanceur, jamais sur le body du client, qui ne nous
 * appartient pas.
 *
 * AU TEMPS FORT, c'est le cartouche qui parle : un filet se tire sous la phrase puis se retire.
 * Sans cartouche (accroche vide ou delai a zero) le temps fort ne rend rien de plus, et c'est
 * assume : Trait est le look ou l'appel passe par le texte.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'trait',

  css: `
/* LE DISQUE EST DU PAPIER, PAS UNE COULEUR : il prend la surface du theme et son filet, donc il
   se lit blanc sur une page claire et sombre sur une page sombre, sans jamais poser du blanc en
   dur sur une page dont on ne sait rien. */
.woot-lanceur[data-look="trait"] .woot-trait__disque {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--woot-surface);
  border: 1px solid var(--woot-filet);
  box-shadow: var(--woot-ombre);
}

.woot-lanceur[data-look="trait"] .woot-trait__figure { position: absolute; inset: 0; }

/* La feuille de dessin deborde de huit pixels sur deux cotes : la signature respire hors du
   disque, comme sur le papier. */
.woot-lanceur[data-look="trait"] .woot-trait__figure svg {
  position: absolute;
  left: -8px;
  bottom: -8px;
  overflow: visible;
}

/* L'ENCRE EST CELLE DU CLIENT TANT QU'ELLE S'ECRIT SUR CE PAPIER. Le disque est la surface du
   theme : une couleur pale y tracait une signature et une croix invisibles, alors --woot-encre-auto
   rend l'encre du theme dans ce cas-la, et la couleur du client dans tous les autres. */
.woot-lanceur[data-look="trait"] .woot-trait__encre,
.woot-lanceur[data-look="trait"] .woot-trait__croix {
  fill: none;
  stroke: var(--woot-encre-auto, var(--client));
  stroke-width: 3.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke-width .3s ease;
}

/* AU REPOS LA SIGNATURE EST CACHEE : le trait est entierement decale hors de sa propre longueur.
   La transition courte est celle du RETOUR (l'effacement a l'ouverture) ; l'ecriture, plus lente,
   est declaree dans la regle qui suit. */
.woot-lanceur[data-look="trait"] .woot-trait__encre {
  stroke-dasharray: 170;
  stroke-dashoffset: 170;
  transition: stroke-dashoffset .6s cubic-bezier(.4, 0, .2, 1), stroke-width .3s ease;
}

.woot-lanceur[data-look="trait"].woot-trait--ecrit:not([data-etat="ouvert"]) .woot-trait__encre {
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 1.7s cubic-bezier(.4, 0, .2, 1) .3s, stroke-width .3s ease;
}

/* L'ECLAT : un court segment clair qui parcourt le trait, toutes les quatre secondes et demie.
   Il ne part qu'apres l'ecriture, sinon il courrait sur un trait qui n'existe pas encore. */
.woot-lanceur[data-look="trait"] .woot-trait__eclat {
  fill: none;
  stroke: #ffffff;
  stroke-width: 1.4;
  stroke-linecap: round;
  opacity: .9;
  stroke-dasharray: 8 162;
  stroke-dashoffset: 170;
  transition: opacity .3s ease;
}

.woot-lanceur[data-look="trait"][data-etat="repos"] .woot-trait__eclat,
.woot-lanceur[data-look="trait"][data-etat="attention"] .woot-trait__eclat {
  animation: woot-trait-brille 4.5s linear 2.2s infinite;
}

@keyframes woot-trait-brille { to { stroke-dashoffset: -170; } }

/* AU SURVOL la plume appuie : le trait s'epaissit, signature comme croix. */
.woot-lanceur[data-look="trait"]:hover .woot-trait__encre,
.woot-lanceur[data-look="trait"]:hover .woot-trait__croix { stroke-width: 4.4; }

/* LE TEMPS FORT DE TRAIT EST DANS LE CARTOUCHE : un filet se tire sous la phrase, puis se retire
   par l'autre bout. C'est un FILET ET NON UN FOND : le seul fond du cartouche reste celui du
   theme, et le look n'y pose que sa ligne. Sans cartouche, il n'y a rien a souligner et le temps
   fort passe sans bruit : Trait s'appuie sur le texte, c'est son parti. */
.woot-lanceur[data-look="trait"] .woot-lanceur__cartouche::after {
  content: "";
  display: block;
  height: 0;
  margin-top: 2px;
  border-top: 1.5px solid var(--client);
  transform: scaleX(0);
  transform-origin: left;
}

.woot-lanceur[data-look="trait"][data-etat="attention"] .woot-lanceur__cartouche::after {
  animation: woot-trait-souligne 1.6s cubic-bezier(.2, .8, .2, 1);
}

@keyframes woot-trait-souligne {
  0% { transform: scaleX(0); }
  35%, 75% { transform: scaleX(1); }
  100% { transform: scaleX(0); transform-origin: right; }
}

/* OUVERT : la signature s'efface (la regle d'ecriture ne s'applique plus), l'eclat s'eteint, et
   la plume trace la croix d'un trait puis de l'autre — les deux delais donnent le geste. */
.woot-lanceur[data-look="trait"] .woot-trait__croix {
  stroke-dasharray: 46;
  stroke-dashoffset: 46;
  transition: stroke-dashoffset .5s cubic-bezier(.4, 0, .2, 1), stroke-width .3s ease;
}

.woot-lanceur[data-look="trait"][data-etat="ouvert"] .woot-trait__croix--un {
  stroke-dashoffset: 0;
  transition-delay: .45s;
}

.woot-lanceur[data-look="trait"][data-etat="ouvert"] .woot-trait__croix--deux {
  stroke-dashoffset: 0;
  transition-delay: .8s;
}

.woot-lanceur[data-look="trait"][data-etat="ouvert"] .woot-trait__eclat { opacity: 0; }
`,

  monter: conteneur => {
    const disque = document.createElement('div');
    disque.className = 'woot-trait__disque';

    const figure = document.createElement('div');
    figure.className = 'woot-trait__figure';
    const signature = 'M22 54c8-24 22-30 30-18 5 8-4 18-12 12-8-6 4-22 20-26';
    figure.innerHTML =
      '<svg viewBox="0 0 80 80" width="80" height="80" aria-hidden="true">' +
      '<path class="woot-trait__encre" d="' +
      signature +
      '"/>' +
      '<path class="woot-trait__eclat" d="' +
      signature +
      '"/>' +
      '<path class="woot-trait__croix woot-trait__croix--un" d="M26 26c9 7 19 19 28 28"/>' +
      '<path class="woot-trait__croix woot-trait__croix--deux" d="M54 26c-9 7-19 19-28 28"/>' +
      '</svg>';

    conteneur.appendChild(disque);
    conteneur.appendChild(figure);

    // L'ECRITURE DEMANDE DEUX INSTANTS DISTINCTS : le conteneur doit d'abord exister avec sa
    // valeur cachee, sinon le navigateur n'a rien a interpoler et la signature apparait d'un bloc.
    // Ce delai court laisse aussi le lanceur etre insere par le modele, qui ajoute apres nous.
    window.setTimeout(() => {
      conteneur.classList.add('woot-trait--ecrit');
    }, 60);
  },

  // TRAIT EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous
  // les looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et sous « moins de mouvement » la transition d'ecriture devient instantanee,
  // ce qui donne exactement le bon resultat : la signature est la, sans le geste.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
